import { useMemo } from 'react'

import { VideoItem } from '@hhgtech/hhg-components'
import { VideoItemType, VideoType } from '@hhgtech/hhg-components/types'
import { Video } from '@hhgtech/icons/other'
import { Box, rem, Stack, useMantineTheme } from '@mantine/core'
import { useIntl } from 'react-intl'

import { getHelloSitesUrl } from 'api/utils/paths'
import CarouselCard from 'components/atoms/CarouselCardV2'
import { domainLocales } from 'domainLocales'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { basePath } from 'interfaces/constants'

import FeatureCard, { FeatureCardProps } from '../FeatureCard'

type Props = Omit<FeatureCardProps, 'children'> & {
  videos?: VideoItemType[]
}

export const HomeVideos = ({ videos }: Props) => {
  const theme = useMantineTheme()
  const { formatMessage: f } = useIntl()
  const { locale } = useRouterExtend()
  const currentDomain = useMemo(() => getHelloSitesUrl(locale), [locale])

  return (
    <FeatureCard url="/videos/" color="yellow">
      <FeatureCard.SectionName
        data-event-category="Recommend Video"
        data-event-action="See All Videos"
        data-event-label={`https://${domainLocales[locale]}/videos/`}
        icon={<Video size={20} color={theme.colors.yellow[6]} />}
      >
        {f({ id: 'homeVideoSection.heading', defaultMessage: 'Video' })}
      </FeatureCard.SectionName>
      <FeatureCard.Heading>
        {f({
          id: 'home.video.title',
          defaultMessage: 'Video',
        })}
      </FeatureCard.Heading>
      <FeatureCard.Content>
        <CarouselCard
          containScroll="trimSnaps"
          slideSize="auto"
          slideGap={12}
          align="start"
          styles={(theme) => ({
            viewport: {
              paddingLeft: 16,
              paddingRight: 16,
              [theme.fn.largerThan('sm')]: {
                paddingLeft: 48,
                paddingRight: 48,
              },
            },
          })}
        >
          <Stack spacing={24}>
            <Box
              sx={(theme) => ({
                marginLeft: -16,
                marginRight: -16,
                [theme.fn.largerThan('sm')]: {
                  marginLeft: -48,
                  marginRight: -48,
                },
              })}
            >
              <CarouselCard.Body>
                {(videos || []).map((video, index) => (
                  <CarouselCard.Slide key={String(index)}>
                    <VideoItem
                      data={video}
                      dataEventCategory="Recommend Video"
                      dataEventAction={`Video Click ${index + 1}`}
                      styles={{
                        root: {
                          borderRadius: 8,
                          width: video.type === VideoType.SHORTS ? 103 : 330,
                          [theme.fn.largerThan('sm')]: {
                            width: video.type === VideoType.SHORTS ? 160 : 515,
                          },
                        },
                        title: {
                          [theme.fn.largerThan('sm')]: {
                            fontSize: rem(14),
                            lineHeight: rem(22),
                          },
                        },
                      }}
                    />
                  </CarouselCard.Slide>
                ))}
              </CarouselCard.Body>
            </Box>
            <Box
              sx={(theme) => ({
                height: 1,
                backgroundColor: '#fff',
                [theme.fn.smallerThan('sm')]: {
                  display: 'none',
                },
              })}
            />
            <CarouselCard.Footer>
              <FeatureCard.ViewAll
                data-event-category="Spotlight"
                data-event-action="See All Click"
                data-event-label={`${currentDomain}${basePath}/videos/`}
              >
                {f({
                  id: 'viewAll',
                  defaultMessage: 'View all',
                })}
              </FeatureCard.ViewAll>
              <CarouselCard.Controls>
                <CarouselCard.Control
                  direction="prev"
                  data-event-category="Recommend Video"
                  data-event-action="See More Video"
                  data-event-label={`${currentDomain}${basePath}`}
                />
                <CarouselCard.Control
                  direction="next"
                  data-event-category={'Recommend Video'}
                  data-event-action="See More Video"
                  data-event-label={`${currentDomain}${basePath}`}
                />
              </CarouselCard.Controls>
            </CarouselCard.Footer>
          </Stack>
        </CarouselCard>
      </FeatureCard.Content>
      <FeatureCard.ViewAll
        data-event-category="Recommend Video"
        data-event-action="See All Videos"
        data-event-label={`${currentDomain}${basePath}/videos/`}
      >
        {f({
          id: 'viewAll',
          defaultMessage: 'View all',
        })}
      </FeatureCard.ViewAll>
    </FeatureCard>
  )
}
